import React from 'react';
import { PublicLayout } from '../../components/PublicLayout';
import { PageHero, BenefitsGrid, HowItWorks, FAQSection, CTASection } from '../../components/PageTemplate';
import { 
  Wrench, Clock, CheckCircle, Users, DollarSign, 
  MessageCircle, Camera, AlertTriangle, Zap 
} from 'lucide-react';

export default function LandlordsMaintenance() {
  const steps = [
    {
      title: 'Tenant Reports Issue',
      description: 'Tenants submit repair requests through their app with photos and descriptions.'
    },
    {
      title: 'AI Triage',
      description: 'Our system categorizes the issue, identifies urgency, and routes appropriately.'
    },
    {
      title: 'Auto-Approve or Escalate',
      description: 'Small repairs auto-approve within your limit. Larger ones come to you for approval.'
    },
    {
      title: 'Job Sent to Contractors',
      description: 'Approved jobs go instantly to vetted local contractors in our network.'
    },
    {
      title: 'First-to-Accept',
      description: 'The first suitable contractor to accept gets the job. Fast response times.'
    },
    {
      title: 'Completion & Payment',
      description: 'Contractor completes work, uploads evidence, invoice processed automatically.'
    }
  ];

  const benefits = [
    {
      icon: Zap,
      title: 'Auto-Approval',
      description: 'Set your auto-approval limit (e.g., £150). Small repairs get fixed without your input.'
    },
    {
      icon: Clock,
      title: 'Fast Response',
      description: 'Emergency repairs (no heating, water) flagged immediately. Non-urgent scheduled appropriately.'
    },
    {
      icon: Users,
      title: 'Vetted Contractors',
      description: 'All contractors in our network are verified, insured, and reviewed by other landlords.'
    },
    {
      icon: Camera,
      title: 'Photo Evidence',
      description: 'Before and after photos required for every job. Full documentation.'
    },
    {
      icon: DollarSign,
      title: 'Transparent Pricing',
      description: 'See quotes upfront. Approve or decline before work starts.'
    },
    {
      icon: MessageCircle,
      title: 'Communication Trail',
      description: 'All messages between tenant, contractor, and you in one thread.'
    },
    {
      icon: AlertTriangle,
      title: 'Over-Budget Handling',
      description: 'If a job exceeds the quote, contractor must get approval before continuing.'
    },
    {
      icon: CheckCircle,
      title: 'Quality Assurance',
      description: 'Tenants confirm work is complete. Disputes handled fairly if issues arise.'
    }
  ];

  const faqs = [
    {
      question: 'How do I set my auto-approval limit?',
      answer: 'In your property settings, you can set an auto-approval limit (e.g., £100, £150, £200). Any repair estimated below this amount will be automatically approved and sent to contractors.'
    },
    {
      question: 'What counts as an emergency repair?',
      answer: 'No heating in winter, no hot water, water leaks, gas leaks, security issues (broken locks/windows). These are flagged as urgent and prioritized.'
    },
    {
      question: 'How are contractors selected?',
      answer: 'Jobs go to all suitable contractors in the area. The first to accept gets the job - this ensures fast response times and competitive pricing.'
    },
    {
      question: 'What if I\'m not happy with the work?',
      answer: 'You can raise a dispute before approving final payment. We mediate between you and the contractor to resolve issues fairly.'
    },
    {
      question: 'Can I use my own contractor?',
      answer: 'Yes - you can invite your preferred contractors to join our network, or handle specific repairs outside the platform (though you lose our tracking and guarantees).'
    }
  ];

  return (
    <PublicLayout>
      <PageHero
        badge="MAINTENANCE & REPAIRS"
        title="Repairs Without"
        subtitle="the Back-and-Forth"
        description="Tenants report issues in-app. Small repairs auto-approve. Larger ones get your approval. Vetted contractors fix things fast."
        primaryCTA={{ href: '/register', label: 'Get Started' }}
        secondaryCTA={{ href: '/landlords', label: 'Back to Overview' }}
        gradient="from-orange-50 via-white to-slate-50"
      />

      <HowItWorks title="How Repairs Work" steps={steps} />

      <BenefitsGrid title="Maintenance Features" benefits={benefits} />

      {/* Auto-Approval Example */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-center mb-8">
            Smart Auto-Approval in Action
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-green-50 rounded-xl p-6 border border-green-200">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-semibold text-green-800">Auto-Approved</span>
              </div>
              <p className="text-sm text-green-700 mb-3">
                &ldquo;Dripping tap in kitchen&rdquo; - Estimated £85
              </p>
              <p className="text-xs text-green-600">
                Below your £150 limit → Sent to contractors automatically
              </p>
            </div>
            <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <span className="font-semibold text-amber-800">Needs Approval</span>
              </div>
              <p className="text-sm text-amber-700 mb-3">
                &ldquo;Boiler not heating water&rdquo; - Estimated £350
              </p>
              <p className="text-xs text-amber-600">
                Above your £150 limit → Waiting for your approval
              </p>
            </div>
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />

      <CTASection
        title="Take the hassle out of maintenance"
        description="Let our system handle the coordination while you stay in control."
        primaryCTA={{ href: '/register', label: 'Get Started' }}
      />
    </PublicLayout>
  );
}
